import type { TFunction } from 'i18next'
import type { SessionPositioning } from '~/gql/positioning/positioning'
import type { LearnerProfileData } from '~/gql/users/learner-profile'
import type { SessionView } from '~/helpers/learner/session-view'
import { AccountStatus, LoginMethod } from '@zeus/index'
import { demoPath } from '~/pages/help/demo/demo-path'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Une fiche apprenant de démonstration (#242), pour la visite du centre d'aide : un
 * parcours en cours (celui de la démo apprenant), une formation terminée avec son
 * attestation, et quelques connexions — de quoi remplir chaque bloc.
 */
export function demoLearner(t: TFunction, now = new Date()) {
  const ago = (days: number) => new Date(now.getTime() - days * DAY_MS)

  const profile: LearnerProfileData = {
    id: 'demo-learner',
    firstName: 'Camille',
    lastName: 'Durand',
    email: 'camille.durand@example.com',
    status: AccountStatus.ENABLED,
    roleName: 'User',
    roleId: 'demo-role',
    createdAt: ago(60),
    groups: [{ id: 'demo-group', name: t('helpDemo.learner.group') }],
    trainingSeconds: 3 * 3600 + 20 * 60,
    loginCount: 9,
    lastLoginAt: ago(1),
    averageDaysBetweenLogins: 3.5,
    completedModules: 2,
    totalModules: 5,
  }

  const course: SessionView = {
    id: 'demo-course',
    name: t('helpDemo.learner.course'),
    progress: { totalModules: 1, completedModules: 1, completed: true },
    certificate: { unlocked: true, reason: null, blockingModuleIds: [] },
    modules: [{
      id: 'demo-course-step',
      position: 0,
      module: { id: 'demo-course-module', title: t('helpDemo.learner.course') },
      myTracking: { completionStatus: 'COMPLETED', scoreRaw: 18, scoreMax: 20, totalTimeSeconds: 45 * 60, completedAt: ago(10) },
    }],
  }

  const logins = [1, 4, 8, 11].map((days, index) => ({
    id: `demo-login-${index}`,
    at: ago(days),
    method: index === 2 ? LoginMethod.MAGIC_LINK : LoginMethod.PASSWORD,
  }))

  // Un positionnement d'entrée et de sortie (#321), pour que la visite montre la comparaison.
  const positioning = [{
    sessionId: 'demo-course',
    sessionName: t('helpDemo.learner.course'),
    entry: {
      answeredAt: ago(20),
      needs: t('helpDemo.learner.positioning.needs'),
      ratings: [{ objective: t('helpDemo.learner.positioning.objective1'), level: 2 }, { objective: t('helpDemo.learner.positioning.objective2'), level: 1 }],
    },
    exit: {
      answeredAt: ago(10),
      needs: null,
      ratings: [{ objective: t('helpDemo.learner.positioning.objective1'), level: 4 }, { objective: t('helpDemo.learner.positioning.objective2'), level: 4 }],
    },
  }] as SessionPositioning[]

  return { profile, sessions: [demoPath(t, now), course] as SessionView[], logins, positioning }
}
